import {useEffect, useRef, useState} from 'react';
import {IncomingQuote, IncomingQuotePage, loadIncomingQuotes} from './incomingQuotesApi';

type Props = {organizationId: string; identityId: string; refreshKey?: number};
type Navigation = {
  scope: string; cursor: string | null; previous: Array<string | null>; sequence: number;
  binding: {revision: number; fingerprint: string} | null;
};
type Observation = {request: string; state: 'loading' | 'unavailable'} | {request: string; state: 'ready'; page: IncomingQuotePage};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const LABEL: Record<IncomingQuote['form_type'], string> = {
  free_quote: 'Quote request', quote_question: 'Quote question', request_service: 'Service request',
};
function firstPage(scope: string, sequence = 0): Navigation {
  return {scope, cursor: null, previous: [], sequence, binding: null};
}

export function IncomingQuotes({organizationId, identityId, refreshKey = 0}: Props) {
  const identityScope = organizationId + ':' + identityId;
  const contextVisit = useRef({identityScope, sequence: 0});
  if (contextVisit.current.identityScope !== identityScope) {
    contextVisit.current = {identityScope, sequence: contextVisit.current.sequence + 1};
  }
  const scope = identityScope + ':' + contextVisit.current.sequence;
  const [navigation, setNavigation] = useState<Navigation>(() => firstPage(scope));
  const request = scope + ':' + navigation.sequence + ':' + (navigation.cursor ?? '') + ':' + refreshKey;
  const [observation, setObservation] = useState<Observation>({request: '', state: 'loading'});
  const validScope = UUID.test(organizationId) && UUID.test(identityId);

  useEffect(() => {
    if (navigation.scope !== scope) { setNavigation(firstPage(scope)); return; }
    if (!validScope) { setObservation({request, state: 'unavailable'}); return; }
    let active = true;
    const controller = new AbortController();
    setObservation({request, state: 'loading'});
    const timer = setTimeout(() => {
      if (!active) return;
      active = false; controller.abort(); setObservation({request, state: 'unavailable'});
    }, 15000);
    loadIncomingQuotes(organizationId, identityId, navigation.cursor, controller.signal).then(page => {
      if (!active) return;
      if ((navigation.binding && (page.source_revision !== navigation.binding.revision || page.source_binding_fingerprint !== navigation.binding.fingerprint))
        || (page.next_cursor !== null && navigation.previous.includes(page.next_cursor))) throw new Error('Incoming quotes are unavailable.');
      active = false; clearTimeout(timer); setObservation({request, state: 'ready', page});
    }).catch(() => {
      if (!active) return;
      active = false; clearTimeout(timer); controller.abort(); setObservation({request, state: 'unavailable'});
    });
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [scope, organizationId, validScope, navigation, request]);

  // Never render a previous identity/organization/page while the next effect is starting.
  const current = validScope && navigation.scope === scope && observation.request === request ? observation : null;
  const page = current?.state === 'ready' ? current.page : null;
  const loading = validScope && (current === null || current.state === 'loading');
  const failed = !validScope || current?.state === 'unavailable';

  function refresh() { setNavigation(firstPage(scope, navigation.sequence + 1)); }
  function navigate(cursor: string | null, previous: Array<string | null>) {
    if (!page) return;
    setNavigation({
      scope, cursor, previous: previous.slice(-100), sequence: navigation.sequence + 1,
      binding: {revision: page.source_revision, fingerprint: page.source_binding_fingerprint},
    });
  }

  function previousPage() {
    const cursor = navigation.previous[navigation.previous.length - 1];
    if (cursor !== undefined) navigate(cursor, navigation.previous.slice(0, -1));
  }

  return <section className="panel" aria-labelledby="incoming-quotes-title">
    <p className="eyebrow">WEBSITE INQUIRIES</p>
    <h2 id="incoming-quotes-title" tabIndex={-1}>Incoming quotes</h2>
    <p>Saved website inquiries for human review. Requester details are sender-declared and unverified. Booking status is not established here.</p>
    <button type="button" onClick={refresh} disabled={loading || !validScope}>Refresh quotes</button>
    <div aria-live="polite" aria-atomic="true">
      {loading && <p className="notice">Checking incoming quotes…</p>}
      {failed && <p className="notice error">Incoming quotes are unavailable. Refresh quotes to check access and availability again.</p>}
      {page?.items.length === 0 && <p className="notice">No saved website inquiries are available on this page.</p>}
    </div>
    {page && <>
      <ol className="intake-list">{page.items.map(quote => <li className="intake-card" key={quote.quote_reference}>
        <p className="workflow-state"><strong>{LABEL[quote.form_type]}</strong> · Saved website inquiry</p>
        <h3>{quote.name_declared}</h3>
        {quote.organization_declared !== '' && <p>Organization (unverified): {quote.organization_declared}</p>}
        <details>
          <summary>Review inquiry</summary>
          <dl>
            <div><dt>Subject (sender-declared)</dt><dd>{quote.subject_declared ?? 'Not available in this summary'}</dd></div>
            <div><dt>Requested area (sender-declared)</dt><dd>{quote.requested_area_declared ?? 'Not available in this summary'}</dd></div>
          </dl>
        </details>
        <p className="reference">Quote reference: {quote.quote_reference}</p>
        <p>Website received: <time dateTime={quote.received_at}>{new Date(quote.received_at).toLocaleString(undefined, {timeZone: 'UTC'})} UTC</time></p>
      </li>)}</ol>
      <nav aria-label="Incoming quote pages">
        <button type="button" disabled={navigation.previous.length === 0} onClick={previousPage}>Previous quote page</button>
        <button type="button" disabled={!page.has_more} onClick={() => navigate(page.next_cursor, [...navigation.previous, navigation.cursor])}>Next quote page</button>
      </nav>
      <p>Each page checks current access. Refresh quotes returns to the first page.</p>
    </>}
  </section>;
}
